// Detects highlight moments from replay event data

import {
  ReplayData,
  ReplayDamageEvent,
  ReplaySpellEvent,
  HighlightMoment,
  PlayerRole,
} from './ReplayTypes';

const MAX_HEALTH = 100;

export function detectHighlights(data: ReplayData): HighlightMoment[] {
  const highlights: HighlightMoment[] = [];
  const events = data.events;

  // Track health for each player across events
  const health: Record<PlayerRole, number> = { player1: MAX_HEALTH, player2: MAX_HEALTH };

  // Track recent spell casts per player for combo detection
  const recentSpells: Record<PlayerRole, { spell: string; timestamp: number }[]> = {
    player1: [],
    player2: [],
  };

  // Track consecutive damage hits per player for combo chain detection
  const consecutiveHits: Record<PlayerRole, number> = { player1: 0, player2: 0 };

  for (let i = 0; i < events.length; i++) {
    const event = events[i];

    if (event.type === 'damage') {
      const dmg = event as ReplayDamageEvent;
      const targetRole = dmg.target;
      const attackerRole: PlayerRole = targetRole === 'player1' ? 'player2' : 'player1';

      health[targetRole] = dmg.newHealth;

      // Clutch Shield: shield blocked damage when player had <20% HP
      if (dmg.shieldAbsorbed > 0 && dmg.amount === 0) {
        const targetHealthBeforeBlock = dmg.newHealth; // health unchanged since damage was 0
        if (targetHealthBeforeBlock < MAX_HEALTH * 0.2) {
          highlights.push({
            timestamp: dmg.timestamp,
            type: 'clutch_shield',
            label: 'Clutch Shield',
            description: `${targetRole === 'player1' ? data.player1Name : data.player2Name} blocked a lethal spell at ${targetHealthBeforeBlock} HP!`,
            player: targetRole,
          });
        }
      }

      // Perfect Block: shield absorbed 100% of spell damage
      if (dmg.shieldAbsorbed > 0 && dmg.amount === 0) {
        highlights.push({
          timestamp: dmg.timestamp,
          type: 'perfect_block',
          label: 'Perfect Block',
          description: `${targetRole === 'player1' ? data.player1Name : data.player2Name} absorbed all ${dmg.shieldAbsorbed} damage with shield!`,
          player: targetRole,
        });
      }

      // Track consecutive hits for combo chain
      if (dmg.amount > 0) {
        consecutiveHits[attackerRole]++;
        consecutiveHits[targetRole] = 0;

        // Combo Chain: 3+ consecutive hits
        if (consecutiveHits[attackerRole] >= 3 && consecutiveHits[attackerRole] % 3 === 0) {
          highlights.push({
            timestamp: dmg.timestamp,
            type: 'combo_chain',
            label: `${consecutiveHits[attackerRole]}-Hit Combo`,
            description: `${attackerRole === 'player1' ? data.player1Name : data.player2Name} landed a ${consecutiveHits[attackerRole]}-hit combo!`,
            player: attackerRole,
          });
        }
      }

      // Last Hit Kill: final blow with both players at <10% HP
      if (dmg.newHealth <= 0 && dmg.amount > 0) {
        const attackerHealth = health[attackerRole];
        if (attackerHealth < MAX_HEALTH * 0.1) {
          highlights.push({
            timestamp: dmg.timestamp,
            type: 'last_hit_kill',
            label: 'Last Hit Kill',
            description: `${attackerRole === 'player1' ? data.player1Name : data.player2Name} clutched the kill at ${attackerHealth} HP!`,
            player: attackerRole,
          });
        }
      }

      // Reflect Kill: check if the killing blow came from a reflected spell
      if (dmg.newHealth <= 0 && dmg.spell === 'reflect') {
        highlights.push({
          timestamp: dmg.timestamp,
          type: 'reflect_kill',
          label: 'Reflect Kill',
          description: `${attackerRole === 'player1' ? data.player1Name : data.player2Name} scored a kill with a reflected spell!`,
          player: attackerRole,
        });
      }
    }

    if (event.type === 'spell_cast') {
      const cast = event as ReplaySpellEvent;
      recentSpells[cast.caster].push({ spell: cast.spell, timestamp: cast.timestamp });

      // Keep only spells within last 4 seconds
      recentSpells[cast.caster] = recentSpells[cast.caster].filter(
        (s) => cast.timestamp - s.timestamp < 4000
      );
    }
  }

  // Deduplicate: if a clutch_shield and perfect_block happen at same timestamp,
  // keep the clutch_shield as it's more interesting
  const deduped: HighlightMoment[] = [];
  const seen = new Set<string>();
  for (const h of highlights) {
    const key = `${h.timestamp}-${h.type}`;
    // Skip perfect_block if there's a clutch_shield at same time
    if (h.type === 'perfect_block') {
      const clutchKey = `${h.timestamp}-clutch_shield`;
      if (seen.has(clutchKey)) continue;
    }
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(h);
    }
  }

  // Sort by timestamp
  deduped.sort((a, b) => a.timestamp - b.timestamp);
  return deduped;
}
